import { getTerminalPositionModifiers } from "../../../../../../../imports/common/PartImportHelpers";
import { DrawRecordTypes, PinDirection, } from "../../../../../../../imports/schematicModels";
import { getNormalizedManufacturerName } from "../../../../../../../part";
import { KiCadHelpers } from "../../../KiCadHelpers";
import { ComponentFieldNumber, DefinitionRecordTypes, FileSectionCommands, newLine, PinElectricalType, SchemaTypes, UserDefinedFieldSchemaTypes, } from "./KiCadLegacySymbolTypes";
/**
 * Imports an Kicad Symbol File and creates a Flux part document.
 *
 * Supoorts EESchema-LIBRARY File Version 4 and below
 */
export class KiCadLegacySymbolParser {
    fileArray;
    userDefinedFieldSchemaType;
    kiCadHelpers;
    constructor(textFileContent) {
        this.kiCadHelpers = new KiCadHelpers("v4");
        this.fileArray = textFileContent.split(newLine);
        this.userDefinedFieldSchemaType = this.getUserDefinedFieldSchemaType(this.fileArray);
    }
    static isLegacySchematicFile(textFileContent) {
        const fileArray = textFileContent.split(newLine);
        return fileArray[0].startsWith(SchemaTypes.library);
    }
    parse() {
        if (!this.isValidKiCadPartLibFile(this.fileArray)) {
            return;
        }
        const partDefinitions = Array.from(this.splitPartLib(this.fileArray).values());
        return partDefinitions.map((partDef) => this.parseKiCadPartData(partDef));
    }
    /**
     * Gets the user defined field schema from the comment lines at the top of
     * the file. This is used to determine what the F3, F4, etc, fields contain.
     */
    getUserDefinedFieldSchemaType(fileArray) {
        for (const line of fileArray) {
            if (line.startsWith(UserDefinedFieldSchemaTypes.SamacSys)) {
                return UserDefinedFieldSchemaTypes.SamacSys;
            }
            if (line.startsWith(UserDefinedFieldSchemaTypes.UltraLibrarian)) {
                return UserDefinedFieldSchemaTypes.UltraLibrarian;
            }
        }
        return UserDefinedFieldSchemaTypes.Unknown;
    }
    splitPartLib(fileArray) {
        const partDefinitions = new Map();
        let startIndexOfPart = -1;
        let currentPartKey;
        let currentPart = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        fileArray.forEach((rowContent, rowIndex) => {
            if (rowContent.startsWith(FileSectionCommands.startDefinition)) {
                startIndexOfPart = rowIndex;
                currentPartKey = this.getDefNameFromRow(rowContent);
            }
            if (startIndexOfPart >= 0) {
                currentPart.push(rowContent);
                if (rowContent.trim() === FileSectionCommands.endDefinition) {
                    partDefinitions.set(currentPartKey, currentPart);
                    startIndexOfPart = -1;
                    currentPart = [];
                    currentPartKey = "";
                }
            }
        });
        return partDefinitions;
    }
    isValidKiCadPartLibFile(fileArray) {
        return fileArray[0].startsWith(SchemaTypes.library);
    }
    parseKiCadPartData(partDefRows) {
        let name = "";
        let manufacturer;
        let description = "";
        let designator;
        let alias;
        let datasheetUrl;
        let mpn;
        let productDetailUrl;
        let productImageUrl;
        let packageCode;
        let footprint;
        let partType;
        let drawMode = false;
        const terminalsData = [];
        const drawInstructions = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        partDefRows.forEach((rowContent) => {
            if (!drawMode) {
                drawMode = rowContent.startsWith(FileSectionCommands.startDraw);
            }
            if (drawMode && rowContent.startsWith(FileSectionCommands.endDraw)) {
                drawMode = false;
            }
            if (!drawMode) {
                const partDefinitions = this.parsePartDefinition(rowContent, name, description, manufacturer, designator, alias, datasheetUrl, mpn, productDetailUrl, productImageUrl, packageCode, footprint, partType);
                name = partDefinitions.name;
                description = partDefinitions.description;
                manufacturer = partDefinitions.manufacturer;
                designator = partDefinitions.designator;
                alias = partDefinitions.alias;
                datasheetUrl = partDefinitions.datasheetUrl;
                mpn = partDefinitions.mpn;
                productDetailUrl = partDefinitions.productDetailUrl;
                productImageUrl = partDefinitions.productImageUrl;
                packageCode = partDefinitions.packageCode;
                footprint = partDefinitions.footprint;
                partType = partDefinitions.partType;
            }
            else {
                if (rowContent.startsWith(DrawRecordTypes.pin)) {
                    drawInstructions.push(this.parsePinDrawInstructionsFromRow(rowContent));
                    terminalsData.push(this.getTerminalDataFromRow(rowContent));
                }
                if (rowContent.startsWith(DrawRecordTypes.rectangle)) {
                    drawInstructions.push(this.parseRectangleDrawInstructionsFromRow(rowContent));
                }
                if (rowContent.startsWith(DrawRecordTypes.circle)) {
                    drawInstructions.push(this.parseCircleDrawInstructionsFromRow(rowContent));
                }
                if (rowContent.startsWith(DrawRecordTypes.arcWithOrigin)) {
                    drawInstructions.push(this.parseArcDrawInstructionsFromRow(rowContent));
                }
                if (rowContent.startsWith(DrawRecordTypes.polyline)) {
                    drawInstructions.push(this.parsePolylineDrawInstructionsFromRow(rowContent));
                }
            }
        });
        return {
            name,
            description,
            symbols: [{ terminalsData, drawInstructions }],
            manufacturer,
            designator,
            alias,
            datasheetUrl,
            mpn,
            productDetailUrl,
            productImageUrl,
            packageCode,
            footprint,
            partType,
        };
    }
    parsePinDrawInstructionsFromRow(rowContent) {
        const pinName = rowContent.split(" ")[1];
        const pinNumber = rowContent.split(" ")[2];
        const posX = rowContent.split(" ")[3];
        const posY = rowContent.split(" ")[4];
        const pinLength = rowContent.split(" ")[5];
        const pinDirection = rowContent.split(" ")[6];
        // Get flip value from the centralized utility for consistent behavior
        const positionModifiers = this.getTerminalPositionModifiersFromRow(rowContent);
        return {
            type: DrawRecordTypes.pin,
            pinName,
            pinNumber: pinNumber,
            posX: this.kiCadHelpers.fromKiCadLinearUnits(Number(posX)),
            posY: this.kiCadHelpers.fromKiCadLinearUnits(Number(posY)),
            pinLength: this.kiCadHelpers.fromKiCadLinearUnits(Number(pinLength)),
            pinDirection,
            flip: positionModifiers.flip,
        };
    }
    parseRectangleDrawInstructionsFromRow(rowContent) {
        const startX = rowContent.split(" ")[1];
        const startY = rowContent.split(" ")[2];
        const endX = rowContent.split(" ")[3];
        const endY = rowContent.split(" ")[4];
        const thickness = rowContent.split(" ")[7];
        return {
            type: DrawRecordTypes.rectangle,
            startX: this.kiCadHelpers.fromKiCadLinearUnits(Number(startX)),
            startY: this.kiCadHelpers.fromKiCadLinearUnits(Number(startY)),
            endX: this.kiCadHelpers.fromKiCadLinearUnits(Number(endX)),
            endY: this.kiCadHelpers.fromKiCadLinearUnits(Number(endY)),
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(thickness))),
        };
    }
    parseCircleDrawInstructionsFromRow(rowContent) {
        const posX = rowContent.split(" ")[1];
        const posY = rowContent.split(" ")[2];
        const radius = rowContent.split(" ")[3];
        const thickness = rowContent.split(" ")[6];
        return {
            type: DrawRecordTypes.circle,
            posX: this.kiCadHelpers.fromKiCadLinearUnits(Number(posX)),
            posY: this.kiCadHelpers.fromKiCadLinearUnits(Number(posY)),
            radius: this.kiCadHelpers.fromKiCadLinearUnits(Number(radius)),
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(thickness))),
        };
    }
    parseArcDrawInstructionsFromRow(rowContent) {
        const posX = rowContent.split(" ")[1];
        const posY = rowContent.split(" ")[2];
        const radius = rowContent.split(" ")[3];
        const startAngle = rowContent.split(" ")[4];
        const endAngle = rowContent.split(" ")[5];
        const thickness = rowContent.split(" ")[8];
        const startX = rowContent.split(" ")[10];
        const startY = rowContent.split(" ")[11];
        const endX = rowContent.split(" ")[12];
        const endY = rowContent.split(" ")[13];
        return {
            type: DrawRecordTypes.arcWithOrigin,
            posX: this.kiCadHelpers.fromKiCadLinearUnits(Number(posX)),
            posY: this.kiCadHelpers.fromKiCadLinearUnits(Number(posY)),
            radius: this.kiCadHelpers.fromKiCadLinearUnits(Number(radius)),
            startAngle: this.kiCadHelpers.fromKiCadAngularUnits(Number(startAngle)),
            endAngle: this.kiCadHelpers.fromKiCadAngularUnits(Number(endAngle)),
            startX: this.kiCadHelpers.fromKiCadLinearUnits(Number(startX)),
            startY: this.kiCadHelpers.fromKiCadLinearUnits(Number(startY)),
            endX: this.kiCadHelpers.fromKiCadLinearUnits(Number(endX)),
            endY: this.kiCadHelpers.fromKiCadLinearUnits(Number(endY)),
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(thickness))),
        };
    }
    parsePolylineDrawInstructionsFromRow(rowContent) {
        const pointCount = rowContent.split(" ")[1];
        const thickness = rowContent.split(" ")[4];
        const points = this.getPolylinePoints(rowContent);
        return {
            type: DrawRecordTypes.polyline,
            pointCount: Number(pointCount),
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(thickness))),
            points,
        };
    }
    getPolylinePoints(rowContent) {
        const rowArray = rowContent.split(" ");
        rowArray.splice(0, 5);
        const points = [];
        let currentPoint = { x: undefined, y: undefined };
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        rowArray.forEach((coordinate, _index) => {
            if (!currentPoint.x) {
                currentPoint.x = this.kiCadHelpers.fromKiCadLinearUnits(Number(coordinate));
            }
            else if (currentPoint.x) {
                currentPoint.y = this.kiCadHelpers.fromKiCadLinearUnits(Number(coordinate));
                points.push({ x: currentPoint.x, y: currentPoint.y });
                currentPoint = { x: undefined, y: undefined };
            }
        });
        return points;
    }
    parsePartDefinition(rowContent, name, description, manufacturer, designator, alias, datasheetUrl, mpn, productDetailUrl, productImageUrl, packageCode, footprint, partType) {
        //
        // Shared
        //
        if (rowContent.startsWith(FileSectionCommands.startDefinition)) {
            name = this.getDefNameFromRow(rowContent);
            mpn = name;
        }
        else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.reference_field}`)) {
            designator = this.getDesignatorFromRow(rowContent);
        }
        else if (rowContent.startsWith(DefinitionRecordTypes.alias)) {
            alias = this.getAliasFromRow(rowContent);
        }
        else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.footprint_field}`)) {
            packageCode = this.getPackageCodeFromRow(rowContent);
            partType = this.getPartTypeFromRow(rowContent);
            footprint = this.getFootprintFromRow(rowContent);
        }
        else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.datasheet_field}`)) {
            datasheetUrl = this.getDatasheetFromRow(rowContent);
        }
        //
        // SamacSys
        //
        if (this.userDefinedFieldSchemaType === UserDefinedFieldSchemaTypes.SamacSys) {
            if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_1}`)) {
                const descriptionContent = this.getContentFromRow(rowContent);
                if (descriptionContent) {
                    description = descriptionContent;
                }
                manufacturer ||= this.getManufacturerFromRow(rowContent);
            }
            else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_3}`)) {
                const manufacturerContent = this.getContentFromRow(rowContent);
                if (manufacturerContent) {
                    manufacturer = this.getContentFromRow(rowContent);
                }
            }
            else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_4}`)) {
                const mpnContent = this.getContentFromRow(rowContent);
                if (mpnContent) {
                    mpn = mpnContent;
                }
            }
        }
        //
        // UltraLibrarian
        //
        else if (this.userDefinedFieldSchemaType === UserDefinedFieldSchemaTypes.UltraLibrarian) {
            if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_1}`)) {
                const mpnContent = this.getContentFromRow(rowContent);
                if (mpnContent) {
                    mpn = mpnContent;
                }
            }
            else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_2}`)) {
                const manufacturerContent = this.getContentFromRow(rowContent);
                if (manufacturerContent) {
                    manufacturer = this.getContentFromRow(rowContent);
                }
            }
            else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_3}`)) {
                const descriptionContent = this.getContentFromRow(rowContent);
                if (descriptionContent) {
                    description = descriptionContent;
                }
                manufacturer ||= this.getManufacturerFromRow(rowContent);
            }
            else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_4}`)) {
                productDetailUrl = this.getContentFromRow(rowContent);
            }
            else if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_5}`)) {
                productImageUrl = this.getContentFromRow(rowContent);
            }
        }
        //
        // Unknown source
        //
        else if (this.userDefinedFieldSchemaType === UserDefinedFieldSchemaTypes.Unknown) {
            // Fallback to the heuristic of searching for the manufacturer in *any* user defined field
            if (rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_1}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_2}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_3}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_4}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_5}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_6}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_7}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_8}`) ||
                rowContent.startsWith(`${DefinitionRecordTypes.field}${ComponentFieldNumber.user_defined_fields_9}`)) {
                description = this.getDescriptionFromRow(description, rowContent);
                manufacturer ||= this.getManufacturerFromRow(rowContent);
            }
        }
        return {
            name,
            description,
            manufacturer,
            designator,
            alias,
            datasheetUrl,
            mpn,
            productDetailUrl,
            productImageUrl,
            packageCode,
            footprint,
            partType,
        };
    }
    getContentString(rowContent) {
        return /(["'])(?:(?=(\\?))\2.)*?\1/.exec(rowContent);
    }
    getContentFromRow(rowContent) {
        const contentString = this.getContentString(rowContent);
        if (contentString) {
            const row = contentString[0];
            return row.substring(1, row.length - 1);
        }
        return;
    }
    getPackageCodeFromRow(rowContent) {
        const rawFootprint = this.getContentFromRow(rowContent);
        return KiCadHelpers.getPackageCodeFromFootprint(rawFootprint);
    }
    getPartTypeFromRow(rowContent) {
        const rawPartType = this.getContentFromRow(rowContent);
        return KiCadHelpers.normalizePartType(rawPartType);
    }
    getFootprintFromRow(rowContent) {
        return this.getContentFromRow(rowContent);
    }
    getDatasheetFromRow(rowContent) {
        const rawDatasheetUrl = this.getContentFromRow(rowContent);
        return KiCadHelpers.cleanupDatasheetUrl(rawDatasheetUrl);
    }
    getDesignatorFromRow(rowContent) {
        return this.getContentFromRow(rowContent);
    }
    getDescriptionFromRow(description, rowContent) {
        // extracts the content string from the row but keep strings within quotes a single item
        const contentString = /(["'])(?:(?=(\\?))\2.)*?\1/.exec(rowContent);
        if (contentString) {
            const row = contentString[0];
            const cleanRow = KiCadHelpers.extractStringFromDoubleQuotes(row).replaceAll("\\n", "").trim();
            if (cleanRow) {
                if (description) {
                    return description + newLine + cleanRow;
                }
                else {
                    return cleanRow;
                }
            }
        }
        return description;
    }
    getManufacturerFromRow(rowContent) {
        const contentArray = this.getContentString(rowContent);
        if (contentArray) {
            const content = contentArray[0];
            const manufacturerName = getNormalizedManufacturerName(content);
            if (manufacturerName) {
                return manufacturerName;
            }
        }
        return undefined;
    }
    getDefNameFromRow(rowContent) {
        return rowContent.split(" ")[1];
    }
    getAliasFromRow(rowContent) {
        const rowSegments = rowContent.split(" ");
        rowSegments.shift();
        return rowSegments;
    }
    /**
     * Converts KiCad's PinDirection enum to the standardized TerminalDirection type.
     * This allows using the centralized getTerminalPositionModifiers utility.
     */
    pinDirectionToTerminalDirection(kiCadParam) {
        switch (kiCadParam) {
            case PinDirection.left:
                return "left";
            case PinDirection.right:
                return "right";
            case PinDirection.up:
                return "up";
            case PinDirection.down:
                return "down";
            default:
                return "right"; // Default to right-facing if unknown
        }
    }
    /**
     * Gets the terminal position modifiers (flip and orientation) for a pin row.
     * Uses the centralized getTerminalPositionModifiers utility for consistent behavior
     * across all importers (KiCad, Eagle, Altium).
     */
    getTerminalPositionModifiersFromRow(rowContent) {
        const kiCadParam = rowContent.split(" ")[6];
        const terminalDirection = this.pinDirectionToTerminalDirection(kiCadParam ?? "");
        return getTerminalPositionModifiers(terminalDirection);
    }
    getTerminalDataFromRow(rowContent) {
        const { pinName: terminalName, pinNumber: terminalNumber, posX, posY, } = this.parsePinDrawInstructionsFromRow(rowContent);
        const terminalType = this.getTerminalType(rowContent);
        const positionModifiers = this.getTerminalPositionModifiersFromRow(rowContent);
        return {
            name: terminalName,
            // eslint-disable-next-line id-blacklist
            number: terminalNumber,
            diagramPosition: {
                x: this.kiCadHelpers.spreadXTerminalPosition(posX),
                y: this.kiCadHelpers.spreadYTerminalPosition(posY),
                flip: positionModifiers.flip,
                ...(positionModifiers.orientation !== undefined && { orientation: positionModifiers.orientation }),
            },
            symbolPosition: {
                x: posX,
                y: posY,
            },
            type: terminalType,
        };
    }
    getTerminalType(rowContent) {
        const identifier = rowContent.split(" ")[11];
        switch (identifier) {
            case PinElectricalType.bidi:
                return "Bi-Directional";
            case PinElectricalType.input:
                return "Input";
            case PinElectricalType.notConnected:
                return "Not Connected";
            case PinElectricalType.openCollector:
                return "Open Collector";
            case PinElectricalType.openEmitter:
                return "Open Emitter";
            case PinElectricalType.output:
                return "Output";
            case PinElectricalType.passive:
                return "Passive";
            case PinElectricalType.powerIn:
                return "Power In";
            case PinElectricalType.powerOut:
                return "Power Out";
            case PinElectricalType.tristate:
                return "Tri-State";
            case PinElectricalType.unspecified:
                return "Unspecified";
            default:
                return "Unspecified";
        }
    }
}
//# sourceMappingURL=KiCadLegacySymbolParser.js.map